import React, { useState, useEffect } from 'react';
import { getShareResume, publishResume, getPublishList } from '../api/housekeeperApi';
import { formatExperienceDate } from '../utils/jsonParser';
import './ShareResumePage.css';

export default function ShareResumePage({ shareToken }) {
  const [shareData, setShareData] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [certUrls, setCertUrls] = useState([]);
  const [workUrls, setWorkUrls] = useState([]);
  const [cookUrls, setCookUrls] = useState([]);
  const [nannyCertUrl, setNannyCertUrl] = useState('');
  const [nannyCertName, setNannyCertName] = useState('育婴师资格证');

  useEffect(() => {
    // Check URL path for share token
    const path = window.location.pathname;
    const tokenFromUrl = path.startsWith('/share/') ? path.replace('/share/', '') : shareToken;
    if (tokenFromUrl) loadShareData(tokenFromUrl);
  }, [shareToken]);

  const loadShareData = async (token) => {
    setLoading(true);
    try {
      const res = await getShareResume(token);
      if (res.data.is_expired) {
        setIsExpired(true);
        setShareData(null);
      } else {
        const data = res.data;
        setShareData(data);
        setLabels(parseLabelStr(data.label));
        setExperiences(parseExperiences(data.housekeeping_experience));
        setCertUrls(parseJsonArray(data.certificate_url));
        setWorkUrls(parseJsonArray(data.work_url));
        setCookUrls(parseJsonArray(data.cook_url));
        setNannyCertUrl(data.nanny_cert_url || '');
        setNannyCertName(data.role === '月嫂' ? '母婴证' : '育婴师资格证');
      }
    } catch (e) {
      console.error('Failed to load share data', e);
    }
    setLoading(false);
  };

  const parseJsonArray = (str) => {
    if (!str) return [];
    try { const arr = JSON.parse(str); return Array.isArray(arr) ? arr : [str]; }
    catch(e) { return str ? [str] : []; }
  };

  const parseLabelStr = (str) => {
    if (!str) return [];
    try { return JSON.parse(str); }
    catch(e) { return str.split(',').map(s => s.trim()); }
  };

  const parseExperiences = (str) => {
    if (!str) return [];
    try { return JSON.parse(str); }
    catch(e) { return [{ date: '', desc: str }]; }
  };

  if (loading) return <div className="share-loading">加载中...</div>;

  if (isExpired) {
    return (
      <div className="share-expired">
        <div className="expired-icon">⏰</div>
        <div className="expired-title-bold">简历已经过期</div>
        <div className="expired-desc">请联系分享人重新获取链接</div>
      </div>
    );
  }

  if (!shareData) {
    return <div className="share-loading">分享链接不存在</div>;
  }

  const roleMap = { '月嫂': '月嫂', '育婴师': '育婴师', '保姆': '保姆', '育儿嫂': '育儿嫂' };
  const maritalMap = { '1': '未婚', '2': '已婚', '未婚': '未婚', '已婚': '已婚' };

  return (
    <div className="share-page">
      {/* Back button */}
      <div className="share-back-header">
        <button className="share-back-btn" onClick={() => window.history.back()}>← 返回</button>
      </div>
      {/* Header with avatar and basic info */}
      <div className="share-header">
        {shareData.photo_url ? (
          <img className="share-avatar" src={shareData.photo_url} alt={shareData.name} />
        ) : (
          <div className="share-avatar-placeholder">{shareData.name?.[0] || '?'}</div>
        )}
        <div className="share-name">{shareData.name}</div>
        <div className="share-role-tag">{roleMap[shareData.role] || shareData.role || '家政阿姨'}</div>
        <div className="share-info-row">
          {shareData.age && <span className="share-info-item">{shareData.age}岁</span>}
          {shareData.service_city && <span className="share-info-item">{shareData.service_city}</span>}
          {shareData.ethnicity && <span className="share-info-item">{shareData.ethnicity}</span>}
          {shareData.education && <span className="share-info-item">{shareData.education}</span>}
          {shareData.marital_status && <span className="share-info-item">{maritalMap[shareData.marital_status] || shareData.marital_status}</span>}
        </div>
      </div>

      {/* Labels */}
      {labels.length > 0 && (
        <div className="share-section">
          <div className="share-section-title">个人标签</div>
          <div className="share-labels">
            {labels.map((l, i) => <span className="share-label-tag" key={i}>{l}</span>)}
          </div>
        </div>
      )}

      {/* Self introduction */}
      {shareData.self_introduction && (
        <div className="share-section">
          <div className="share-section-title">自我介绍</div>
          <div className="share-intro-content">{shareData.self_introduction}</div>
        </div>
      )}

      {/* Work experience */}
      {experiences.length > 0 && (
        <div className="share-section">
          <div className="share-section-title">工作经历</div>
          {experiences.map((exp, i) => (
            <div className="share-exp-item" key={i}>
              <div className="share-exp-date">{formatExperienceDate(exp.date)}</div>
              <div className="share-exp-desc">{exp.desc}</div>
            </div>
          ))}
        </div>
      )}

      {/* Nanny Certificate */}
      {nannyCertUrl && (
        <div className="share-section">
          <div className="share-section-title">{nannyCertName}</div>
          <div className="share-photo-grid-single">
            <img className="share-photo-item-large" src={nannyCertUrl} alt={nannyCertName} />
          </div>
        </div>
      )}

      {/* Certificate images */}
      {certUrls.length > 0 && (
        <div className="share-section">
          <div className="share-section-title">技能证书</div>
          <div className="share-photo-grid">
            {certUrls.map((url, i) => (
              <img key={i} className="share-photo-item" src={url} alt={`证书${i + 1}`} />
            ))}
          </div>
        </div>
      )}

      {/* Work photos */}
      {workUrls.length > 0 && (
        <div className="share-section">
          <div className="share-section-title">工作照片</div>
          <div className="share-photo-grid">
            {workUrls.map((url, i) => (
              <img key={i} className="share-photo-item" src={url} alt={`工作${i + 1}`} />
            ))}
          </div>
        </div>
      )}

      {/* Cook photos */}
      {cookUrls.length > 0 && (
        <div className="share-section">
          <div className="share-section-title">厨艺展示</div>
          <div className="share-photo-grid">
            {cookUrls.map((url, i) => (
              <img key={i} className="share-photo-item" src={url} alt={`厨艺${i + 1}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}